import { Component, inject, OnInit } from '@angular/core';
import { BaryModalEvent, IBaryModalEvent } from '../../../../../models/bary-modal-event';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BaryComponentHelper } from '../../../../shared/util/bary-component-helper';
import { IMyDate, MDBModalRef } from 'ng-uikit-pro-standard';
import { EstimateService } from '../../../../../services/estimate.service';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { EstimationStatus, LastEstimate } from '../../../../../models/estimate-result-bundle.model';
import dayjs from 'dayjs';
import { QuotationView } from '../../../../../models/quotation-view.model';
import { ObjLabelValue } from '../../../../../models/obj-label-value.interface';
import { LIST_VEHICLE_CATEGORY, LIST_VEHICLE_TYPE, Vehicle, VehicleCategory, VehicleType } from '../../../../../models/vehicle.model';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'jhi-bary-modal-transporter-respond-quotation',
  templateUrl: './bary-modal-transporter-respond-quotation.component.html',
  styleUrls: ['./bary-modal-transporter-respond-quotation.component.scss'],
})
export class BaryModalTransporterRespondQuotationComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #estimateService = inject(EstimateService);
  readonly #translateService = inject(TranslateService);

  private action = BaryComponentHelper.generateAction<IBaryModalEvent>();

  public loaded = true;
  public priceIsUpdated = false;

  public lastEstimate: LastEstimate = new LastEstimate({ status: EstimationStatus.QUOTATION_RESPONSE, estimation: { basePrice: 0 } });

  public quotation!: QuotationView;
  public quotationForm!: FormGroup;
  public pickupOptions!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate: IMyDate };
  public deliveryOptions!: { minDate: IMyDate; maxDate: IMyDate; disableUntilDate: IMyDate };

  public categories: Array<ObjLabelValue> = Object.values(LIST_VEHICLE_CATEGORY).map(
    (v: VehicleCategory): ObjLabelValue => ({
      label: this.#translateService.instant(v.labelKey),
      value: v.code,
    })
  );

  public types!: Array<ObjLabelValue>;

  public vehicles: Vehicle[] = [];

  public handleClick(from: any): void {
    this.action.next(new BaryModalEvent(from));
    this.#modalRef.hide();
  }

  public ngOnInit(): void {
    this.types = Object.values(LIST_VEHICLE_TYPE)
      .filter(
        v =>
          v.merchandiseType === this.quotation.merchandiseType.code &&
          ((this.quotation.isControlledTemperature && v.isControlledTemperature != false) || (!this.quotation.isControlledTemperature && !v.isControlledTemperature))
      )
      .map(
        (v: VehicleType): ObjLabelValue => ({
          label: this.#translateService.instant(v.labelKey),
          value: v.code,
        })
      );

    this.categories.push({
      label: this.#translateService.instant('LABEL.VEHICLE.EMPTY_CATEGORY'),
      value: '',
    });

    this.addNoVehicleType();
    this.initForm();
    this.initListeners();
  }

  public initForm(): void {
    const today = dayjs(new Date()).startOf('date');
    const pickupDate = this.quotation.pickupDate!;
    const deliveryDate = this.quotation.deliveryDate!;
    const deliveryMinusPickupInDays = dayjs(deliveryDate).diff(dayjs(pickupDate), 'day');
    const newDeliveryDate = deliveryMinusPickupInDays > 0 ? dayjs(today).add(deliveryMinusPickupInDays, 'day') : null;

    this.pickupOptions = {
      minDate: { year: today.year(), month: today.month() + 1, day: today.date() },
      maxDate: {
        year: today.year(),
        month: today.month() + 3,
        day: today.date(),
      },
      disableUntilDate: { year: today.year(), month: today.month() + 1, day: today.date() },
    };
    this.deliveryOptions = {
      minDate: { year: today.year(), month: today.month() + 1, day: today.date() - 1 },
      maxDate: {
        year: today.year(),
        month: today.month() + 3,
        day: today.date(),
      },
      disableUntilDate: { year: today.year(), month: today.month() + 1, day: today.date() - 1 },
    };

    this.quotationForm = new FormGroup(
      {
        pickupDate: new FormControl(today, [Validators.required]),
        deliveryDate: new FormControl(newDeliveryDate, [Validators.required]),
        quotationBasePrice: new FormControl(this.quotation.quotationBasePrice, [Validators.required, Validators.min(0.01)]),
        quotationValidityDate: new FormControl(this.quotation.quotationValidityDate ?? today.add(7, 'day'), [Validators.required]),
        quotationPriceHT: new FormControl(this.quotation.quotationPriceHT, [Validators.required, Validators.min(0.01)]),
      },
      {
        validators: [this.dateValidator()],
      }
    );

    if (this.quotation.isFTL()) {
      this.quotationForm.addControl('vehicleCategory', new FormControl(''));
      this.quotationForm.addControl('vehicleType', new FormControl({ value: '', disabled: true }, []));
    }
  }

  public initListeners(): void {
    if (this.quotation.isFTL()) {
      this.quotationForm.controls['vehicleCategory'].valueChanges.subscribe({
        next: (categorySelected: string): void => {
          const vehicleTypeControl = this.quotationForm.controls['vehicleType'];
          if (categorySelected !== '') {
            this.types = this.types.filter(type => type.value !== '');
            vehicleTypeControl.addValidators(Validators.required);
            vehicleTypeControl.setValue(this.types[0].value);
            vehicleTypeControl.enable();
          } else {
            this.addNoVehicleType();
            vehicleTypeControl.clearValidators();
            vehicleTypeControl.setValue('');
            vehicleTypeControl.disable();
          }
          vehicleTypeControl.updateValueAndValidity();
          vehicleTypeControl.markAsTouched();
          vehicleTypeControl.markAsDirty();
        },
      });
    }

    this.refreshEstimate();
  }

  private refreshEstimate(): void {
    const pickupDateCtrl = this.quotationForm.get('pickupDate');
    const deliveryDateCtrl = this.quotationForm.get('deliveryDate');
    const basePriceCtrl = this.quotationForm.get('quotationBasePrice');
    const validityDateCtrl = this.quotationForm.get('quotationValidityDate');
    const vehicleTypeCtrl = this.quotationForm.get('vehicleType');
    const vehicleCategoryCtrl = this.quotationForm.get('vehicleCategory');

    combineLatest([
      pickupDateCtrl!.valueChanges.pipe(startWith(pickupDateCtrl!.value), distinctUntilChanged()),
      deliveryDateCtrl!.valueChanges.pipe(startWith(deliveryDateCtrl!.value), distinctUntilChanged()),
      basePriceCtrl!.valueChanges.pipe(startWith(basePriceCtrl!.value), distinctUntilChanged(), debounceTime(500)),
      validityDateCtrl!.valueChanges.pipe(startWith(validityDateCtrl!.value), distinctUntilChanged()),
    ]).subscribe(([pickupDate, deliveryDate, basePrice, validityDate]): void => {
      this.loaded = false;
      this.priceIsUpdated = false;

      if (pickupDate && deliveryDate && basePrice) {
        if (this.quotation.isFTL()) {
          this.quotation.items[0].vehicleType = vehicleTypeCtrl!.value !== '' ? LIST_VEHICLE_TYPE[vehicleTypeCtrl!.value] : undefined;
          this.quotation.items[0].vehicleCategory = vehicleCategoryCtrl!.value !== '' ? LIST_VEHICLE_CATEGORY[vehicleCategoryCtrl!.value] : undefined;
        }

        this.quotation.pickupDate = pickupDate;
        this.quotation.deliveryDate = deliveryDate;
        this.quotation.quotationBasePrice = basePrice;
        this.quotation.quotationValidityDate = validityDate;

        this.#estimateService.refreshPricing(this.quotation, this.quotation.customerId!).subscribe((estimation: LastEstimate) => {
          this.lastEstimate = estimation;
          this.quotationForm.patchValue({ quotationPriceHT: estimation.estimation.priceHT });
          this.lastEstimate.status = EstimationStatus.QUOTATION_RESPONSE;
          this.priceIsUpdated = true;
        });
      }
    });
  }

  private saveValue(): void {
    if (this.quotation.isFTL()) {
      const type = this.quotationForm.get('vehicleType')?.value;
      this.quotation.items[0].vehicleType = type !== '' ? LIST_VEHICLE_TYPE[type] : undefined;
      const cat = this.quotationForm.get('vehicleCategory')?.value;
      this.quotation.items[0].vehicleCategory = cat !== '' ? LIST_VEHICLE_CATEGORY[cat] : undefined;
    }
    this.quotation.pickupDate = this.quotationForm.get('pickupDate')?.value;
    this.quotation.deliveryDate = this.quotationForm.get('deliveryDate')?.value;
    this.quotation.quotationBasePrice = this.quotationForm.get('quotationBasePrice')?.value;

    const validityDate = this.quotationForm.get('quotationValidityDate')?.value;
    if (validityDate) {
      this.quotation.quotationValidityDate = dayjs(validityDate).hour(23).minute(59).second(59).millisecond(0);
    }

    this.quotation.quotationPriceHT = this.quotationForm.get('quotationPriceHT')?.value;
  }

  public getControl(controlName: string): FormControl {
    return this.quotationForm.get(controlName) as FormControl;
  }

  public submit(): void {
    if (this.quotationForm.invalid) {
      this.quotationForm.markAsTouched();
      return;
    }
    this.saveValue();
    this.action.next(new BaryModalEvent({ submit: true }));
    this.#modalRef.hide();
  }

  public getNbDay(date: Date): number {
    const dayJsDate = dayjs(date);
    if (dayJsDate.date() === dayjs().date()) {
      return 0;
    }
    return dayjs(date).diff(dayjs(), 'day') + 1;
  }

  private dateValidator(): ValidatorFn {
    return (controls: any): ValidationErrors | null => {
      const pickupDate = controls.get('pickupDate');
      const deliveryDate = controls.get('deliveryDate');
      const today = dayjs(new Date()).startOf('date');
      if (pickupDate?.value < today || deliveryDate?.value <= today || pickupDate?.value > deliveryDate?.value) {
        return {
          errorDateForm: true,
        };
      }
      return null;
    };
  }

  private addNoVehicleType(): void {
    this.types = [...this.types, { label: this.#translateService.instant('LABEL.VEHICLE.EMPTY_TYPE'), value: '' }];
  }
}
